import * as React from 'react';
import { CompanyDetailedDto } from 'models/dtoTypes';
import { styled } from '@mui/material/styles';
import { cloneDeep } from 'lodash';
import { UpdateScoring, PollCompanyScore } from 'services/openapiClient';
import RawIconButton from '@mui/material/IconButton';
import CircularProgress from '@mui/material/CircularProgress';
import BlockIcon from '@mui/icons-material/Block';
import DownloadIcon from '@mui/icons-material/Download';
// import { dateAdd } from 'utils/date';
// import UpdateIcon from '@mui/icons-material/Update';

const IconButton = styled(RawIconButton)({
  fontSize: 50,
});

interface OnComplete {
  (company: CompanyDetailedDto): void;
}

interface OnTrigger {
  (id: string): void;
}

interface Props {
  // older than 90 days, will show trigger button ignore the score
  lastUpdate?: string | null;
  company: CompanyDetailedDto;
  initialState: string;
  initialScore: number;
  disabled?: boolean; // cannot request update
  onComplete?: OnComplete;
  onTrigger?: OnTrigger;
  onStarted: () => void;
}

export default function EsgScoreUpdateButton(props: Props): JSX.Element {
  const timerRef = React.useRef<ReturnType<typeof setInterval> | null>(null);
  const POLL_INTERVAL = 5000; // poll company result every 5 seconds

  const { initialState, initialScore, company, onComplete, onTrigger, onStarted, disabled } = props;
  const companyId = company?.id;
  const [scoringState, setScoringState] = React.useState<string>(initialState);

  React.useEffect(() => {
    setScoringState(initialState);
    if (initialState === 'InProgress') {
      startPoll();
    }
  }, [initialState]);

  React.useEffect(() => {
    return () => {
      // clear setInterval on unmount
      console.log(`stop polling: ${companyId || 'unknown Id'}`);
      stopPoll();
    };
  }, []);

  const startPoll = () => {
    const timer = timerRef.current;
    if (timer) {
      clearInterval(timer);
    }
    timerRef.current = setInterval(handlePollCompanyScore, POLL_INTERVAL);
  };

  const stopPoll = () => {
    const timer = timerRef.current;
    if (timer) {
      console.log('stop polling company', companyId);
      clearInterval(timer);
      timerRef.current = null;
    }
  };

  const handlePollCompanyScore = () => {
    if (!companyId) {
      setScoringState('NotStarted');
      return;
    }
    PollCompanyScore(companyId)
      .then((response) => {
        if (!response) {
          // poll failed, rollback
          setScoringState('NotStarted');
          return;
        }
        const company = response.data;

        // got the updated company
        switch (company.scoringState) {
          case 'NotStarted':
          case 'InProgress': {
            console.log('updated: ', company.scoringState);
            break;
          }
          case 'Completed': {
            handleCompleteUpdate(company);
            break;
          }
          default:
            console.error('Unknown state');
        }
      })
      .catch(() => {
        handleError();
      });
  };

  const handleCompleteUpdate = (company: CompanyDetailedDto) => {
    stopPoll();
    onComplete && onComplete(company);
  };

  const handleError = () => {
    const newCompany = cloneDeep(company);
    newCompany.scoringState = 'Error';
    stopPoll();
    onComplete && onComplete(newCompany);
  };

  const handleStartUpdate = async (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    if (!companyId) return;
    onStarted();
    const updated = await UpdateScoring({
      id: companyId,
      notifyUser: false,
    });
    const { statusCode } = updated;
    if (statusCode === 200) {
      // start polling every 5 seconds
      onTrigger && onTrigger(companyId);
      setScoringState('InProgress');
      startPoll();
    } else {
      // trigger failed, rollback
      stopPoll();
      setScoringState('NotStarted');
    }
  };

  const renderTriggerButton = (error = false) => {
    if (disabled) {
      return (
        <IconButton
          onClick={(e: React.MouseEvent<HTMLElement>) => e.stopPropagation()}
          color="inherit"
          aria-label="block"
          size="large"
        >
          <BlockIcon fontSize="inherit" />
        </IconButton>
      );
    }
    return (
      <IconButton onClick={handleStartUpdate} color="inherit" aria-label="delete" size="large">
        <DownloadIcon fontSize="inherit" color={error ? 'error' : undefined} />
      </IconButton>
    );
  };

  const renderInProgress = () => {
    return (
      <div style={{ marginTop: 15 }}>
        <CircularProgress color="inherit" size={40} />
      </div>
    );
  };

  const renderCompleted = () => {
    return <span style={{ fontSize: 40, lineHeight: 2 }}>{initialScore.toString()}</span>;
  };

  // score lastUpdate older than 90 days ago
  // if (lastUpdate && new Date(lastUpdate) < dateAdd(lastUpdate, -90)) {
  //  return renderTriggerButton();
  // }
  const renderScoreBox = () => {
    switch (scoringState) {
      case 'InProgress':
        return renderInProgress();
      case 'Completed':
        return renderCompleted();
      case 'Error':
        return renderTriggerButton(true);
      case 'NotStarted':
      default:
        return renderTriggerButton();
    }
  };

  return renderScoreBox();
}
